import React, { ReactNode } from "react";
import * as R from "ramda";
import { useStorage } from "@natera/platform/lib/hooks";
import { Notification } from "@app/components";
import { NotificationType } from "@app/components/notification/notification";
import { defineMessages, useIntl } from "react-intl";
import { datadogRum } from "@datadog/browser-rum";
import { MessageDescriptor } from "@formatjs/intl";

type NotificationId = string;

export type Clear = () => void;

export interface AddNotificationParameters {
  id?: string;
  title?: React.ReactNode;
  message?: React.ReactNode;
  type?: NotificationType;
  errorTitle?: MessageDescriptor;
  errorMessage?: MessageDescriptor;
  actions?: React.ReactNode;
  hasTimeout?: boolean;
  ms?: number;
  // Survives clear() (route changes, ?code= handling, timeouts). For messages
  // that must land after an intentional redirect — the owner removes them by id
  // once handled (e.g. on resubmit). Clear via removeNotification(id).
  sticky?: boolean;
}

export type AddNotification = (
  params: AddNotificationParameters
) => NotificationId;

export type RemoveNotification = (id: NotificationId) => void;

export interface NotificationController {
  clear: Clear;
  addNotification: AddNotification;
  removeNotification: RemoveNotification;
  notifications: React.ReactNode;
}

interface NotificationProvider {
  timeout?: number;
}

interface StorageNotification extends AddNotificationParameters {
  id: NotificationId;
}

export const Context = React.createContext<NotificationController>({
  clear: () => undefined,
  addNotification: () => "0",
  removeNotification: () => undefined,
  notifications: undefined,
});

const messages = defineMessages({
  commonErrorText: {
    id: "commonErrorText",
    defaultMessage: "We're sorry. Something went wrong.",
  },
});

const COMMON_ERROR_ID = "COMMON_ERROR_ID";

Context.displayName = "NotificationContext";

const NotificationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const intl = useIntl();

  const notificationStorage = useStorage<StorageNotification>({
    initialRecords: [],
  });
  const idSeq = React.useRef(0);

  // Sticky notifications survive clear() so a message can outlive an intentional
  // redirect; their owner removes them by id (removeNotification) once handled.
  const clearNonSticky = () => {
    notificationStorage
      .getRecords()
      .filter((record) => !record.sticky)
      .forEach((record) => notificationStorage.removeRecord(record.id));
  };

  const clear: Clear = () => {
    clearNonSticky();
  };

  const addNotification: AddNotification = (
    params: AddNotificationParameters
  ) => {
    let id: string;
    const isCommonError = !params.message && !params.title;
    if (params.id) {
      id = params.id;
    } else if (isCommonError) {
      id = COMMON_ERROR_ID;
      // send datadog RUM error
      datadogRum.addError(new Error("Default error notification displayed."), {
        params: params,
      });
    } else {
      idSeq.current += 1;
      id = `${idSeq.current}`;
    }

    const notification = notificationStorage.getRecord(id);
    if (notification) {
      return id;
    }

    notificationStorage.addRecord({
      id,
      title: params.title,
      message: params.message,
      type: params?.type || "info",
      errorTitle: params.errorTitle,
      errorMessage: params.errorMessage,
      actions: params?.actions,
      sticky: params.sticky,
    });

    window.scrollTo(0, 0);

    if (params.hasTimeout) {
      const timeout = params.ms ? params.ms : 5000;
      setTimeout(() => {
        clearNonSticky();
      }, timeout);
    }

    return id;
  };

  const removeNotification: RemoveNotification = (id) => {
    notificationStorage.removeRecord(id.toString());
  };

  const notificationArray = R.map(([id, notification]) => {
    const getNotificationTitle = () => {
      if (notification.title || notification.errorTitle) {
        return notification.errorTitle
          ? intl.formatMessage(notification.errorTitle)
          : notification.title;
      }
    };

    const getNotificationMessage = () => {
      if (!notification.message && !notification.title) {
        return intl.formatMessage(messages.commonErrorText);
      } else if (notification.message) {
        return notification.errorMessage
          ? intl.formatMessage(notification.errorMessage)
          : notification.message;
      }
    };

    const title = getNotificationTitle();
    const message = getNotificationMessage();

    return (
      <Notification
        key={id}
        type={notification.type}
        titleElem={title}
        actions={notification.actions}
      >
        {message}
      </Notification>
    );
  })(
    R.zip(
      R.keys(notificationStorage.getRecords()),
      R.values(notificationStorage.getRecords())
    )
  );

  const notifications = (
    <div className="mobile-view__container" role="alert">
      {notificationArray}
    </div>
  );

  return (
    <Context.Provider
      value={{
        clear,
        addNotification,
        removeNotification,
        notifications,
      }}
    >
      {children}
    </Context.Provider>
  );
};

export default NotificationProvider;
